import React, { useContext, useEffect, useMemo, useState } from "react";
import WrapperPage from "../../components/WrapperPage";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import useViewController from "./useViewController";
import ZoneItem from "./components/ZoneItem";
import ZonePersonal from "./components/ZonePersonal";
import SwiperModal from "../../modals/SwiperModal";
import Results from "./components/Results";
import useController from "./useController";
import Game from "../../store/Game";
import Points from "../../store/Points";
import BaseSpinner from "../../components/BaseSpinner";
import { demonstrationStore } from "../../store/Demonstration";
import { shopStore } from "../../store/Shop";
import { MODAL_TYPES } from "../../utils/emitter";
import BuyModal from "../../modals/BuyModal";
import useModalActions from "../../hooks/useModalActions";
import ROUTES from "../../utils/routes.js";
import "./style.scss";
// fix CI: `disableRubberBand` в utils/disableRubberBand НЕ экспортируется (там
// disableRubberBandScroll/enableRubberBandScroll) и в этом файле не использовался.
// Мёртвый импорт webpack раньше отдавал как warning, но после обновления зависимостей
// (Dockerfile ставит их без локфайла: `npm install --only=prod`) он стал ошибкой и
// ронял прод-сборку: «Attempted import error… Failed to compile».
import { enableRubberBandScroll } from "../../utils/disableRubberBand";
import INTOUCH_API from "../../services/Api";
import { SOCKET_EVENTS, SocketContext } from "../../components/SocketProvider";
import useCurrentUserData from "../../hooks/useCurrentUserData";

export default observer(function DemoResultTestPage() {
  const { sendEvent } = useContext(SocketContext);
  const { t } = useTranslation();
  useViewController();
  const { checkCard, createInsightForm, navigate, isInsightLoading } =
    useController();
  const { openModal } = useModalActions();
  const { getUserData } = useCurrentUserData();

  const game = Game.game;

  // Task 1: congrats-попап +500 показываем ЗДЕСЬ — когда игрок нажал «Смотреть» и попал
  // на результаты. Начисление приходит по сокету при завершении теста, но сам попап
  // откладывается (Points.pendingCongrats в Etap2SocketBridge) до этого экрана.
  useEffect(() => {
    const pending = Points.consumePendingCongrats();
    if (pending) {
      openModal(MODAL_TYPES.POINTS_CONGRATS, pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const gameEvent = Game.endGameEvent;
      const event = gameEvent.find((e) => e.gameId === game.id);
      if (event) {
        await INTOUCH_API.requestManager("game_notification", {
          id: event.id,
          isViewed: true,
        });
        await INTOUCH_API.requestManager(
          "get_game_events",
          null,
          (response) => {
            Game.setGameEventsData(response.data);
          }
        );
      }
    })();
  }, []);

  // item 11: по завершению демо-подсказки включаем rubber-band. Поп-ап «купи полную
  // версию» БОЛЬШЕ не открываем здесь — на первом заходе в результаты остаётся только
  // поп-ап +1000 баллов (он приходит по сокету).
  // hotfix: НЕ вызываем resetDemonstration здесь — сброс делал finished=false, и для
  // PREMIUM-игр демо могло повторно открыться (в оригинале сброс был только в ветке
  // BUY_GAME не-PREMIUM). finished должен остаться true; естественный сброс — при
  // уходе со страницы (Demonstration/index.js).
  useEffect(() => {
    if (demonstrationStore.finished) {
      enableRubberBandScroll();
    }
  }, [demonstrationStore.finished]);

  // item 11: поп-ап «Полная версия игры» = мягкое НАПОМИНАНИЕ. Показываем его только
  // если игрок уже заходил в результаты >= 3 раз и всё ещё не купил игру (не PREMIUM).
  // Счётчик заходов — per-game в localStorage; на 1-м/2-м заходе поп-апа нет.
  useEffect(() => {
    if (!game?.id) return;
    const key = `wepurplay_results_views_${game.id}`;
    const views = (+localStorage.getItem(key) || 0) + 1;
    localStorage.setItem(key, String(views));
    if (views >= 3 && game.type !== "PREMIUM" && !shopStore.isOpen) {
      openModal(MODAL_TYPES.BUY_GAME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.id]);

  const checkTogetherAnsweredQuestionaries = async (card) => {
    try {
      const response = await INTOUCH_API.requestManager("getInsightForms", {
        payload: game.id,
      });

      const forms = response.data;

      if (!forms || !forms.length) return false;

      const user = getUserData();
      const togetherAnsweredQuestionaries = forms.filter((questionarie) =>
        questionarie.every((question) => question.insightFormAnswers.length > 0)
      );

      const togetherAnsweredQuestion = togetherAnsweredQuestionaries.reduce(
        (acc, questionarie) => {
          const filteredQuestion = questionarie.filter(
            (question) => question.questionId === card.questionId
          );

          return [...acc, ...filteredQuestion];
        },
        []
      );

      const userQuestion = togetherAnsweredQuestion.find(
        (form) => form.userId === user.id
      );
      const hasTogetherAnswere = togetherAnsweredQuestionaries.some(
        (questionarie) =>
          questionarie.find(
            (question) => question.questionId === card.questionId
          )
      );
      if (hasTogetherAnswere) {
        navigate(
          ROUTES.HISTORY_INSIGHT_QUESTIONNAIRE_FORM_PAGE(userQuestion.id),
          {
            state: {
              formId: userQuestion.id,
            },
          }
        );

        return hasTogetherAnswere;
      }
    } catch (e) {
      return false;
    }
  };

  const handleClickInsightForm = async (card) => {
    const hasTogetherAnswer = await checkTogetherAnsweredQuestionaries(card);
    if (!hasTogetherAnswer) {
      // item 17.09: см. выше — раздел анкеты одинаков независимо от покупки
      game && game.type === "DEMO"
        ? navigate(ROUTES.INSIGHT_QUESTIONNAIRE_PAGE)
        : createInsightForm(card);
    }
  };

  return (
    <div className="demo-result-wrapper">
      <BuyModal />
      <SwiperModal
        isInsightLoading={isInsightLoading}
        onClickInsightForm={(card) => handleClickInsightForm(card)}
        disabledClick={game && game.type === "DEMO"}
        gameFormat={game && game.type === "DEMO" ? "" : "marathon"}
        selectCard={checkCard}
        onSelectConfig={{
          slideOnSelect: false,
        }}
      />
      <WrapperPage
        backBtn
        onClickBackBtn={() => navigate(ROUTES.PERSONAL_ACCOUNT_PAGE)}
        burgerMenu
        headerTitle={t(
          game && game.type === "DEMO"
            ? "demo_result_test_page.title"
            : "demo_result_test_page.titleInTouch"
        )}
      >
        {Game.isZonesLoading ? (
          <div className="demo-result-wrapper__loader">
            <BaseSpinner />
          </div>
        ) : (
          <>
            <div className="demo-result-wrapper__zones">
              {Game.zones.map((item, i) => {
                if (item.kind === "personal") {
                  // 7-я зона «Личные запросы» — свой рендер (список BLANK-анкет)
                  return <ZonePersonal key={item.title} item={item} />;
                }
                return (
                  <ZoneItem
                    checkCard={checkCard}
                    isDemo={game && game.type === "DEMO"}
                    key={item.title}
                    item={item}
                    indexZone={i}
                    gameId={game.id}
                  />
                );
              })}
            </div>
            <Results isDemo={game && game.type === "DEMO"} />
          </>
        )}
      </WrapperPage>
    </div>
  );
});
